import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Heart,
  PackageCheck,
  ShieldCheck,
  Sparkles,
  Star,
  Truck,
} from "lucide-react";

import { apiRequest } from "../services/api";
import { PRODUCTS } from "../data/products";

import PageWrapper from "./common/PageWrapper";
import CategoryCard from "./common/CategoryCard";
import { ProductCard } from "./common/ProductCard";
import { SkeletonCard } from "./common/SkeletonCard";

import "swiper/css";
import "swiper/css/pagination";

const getImageUrl = (image) => {
  if (!image) return null;

  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  const baseUrl = (
    process.env.REACT_APP_ASSET_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).replace(/\/$/, "");

  return `${baseUrl}${image.startsWith("/") ? "" : "/"}${image}`;
};

const CAMPAIGNS = [
  {
    eyebrow: "The CHARMS Edit",
    title: "Style that feels like you.",
    description:
      "Discover the newest premium fashion collections from the brands you love.",
    image:
      "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1800&q=90",
    primaryLabel: "Shop Women",
    primaryPath: "/category/womens",
    secondaryLabel: "Shop Men",
    secondaryPath: "/category/mens",
    align: "left",
  },
  {
    eyebrow: "New Season",
    title: "Elevated essentials, made to be worn.",
    description:
      "Fresh arrivals for every plan, every mood and every occasion.",
    image:
      "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=1800&q=90",
    primaryLabel: "Explore New Arrivals",
    primaryPath: "/search/all?sort=newest",
    secondaryLabel: "Shop Kids",
    secondaryPath: "/category/kids",
    align: "right",
  },
  {
    eyebrow: "Premium Brands",
    title: "Your favourite labels, all in one place.",
    description:
      "Curated fashion, authentic products and a shopping experience made personal.",
    image:
      "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1800&q=90",
    primaryLabel: "Shop Collection",
    primaryPath: "/search/all",
    secondaryLabel: "View Brands",
    secondaryPath: "/search/all",
    align: "left",
  },
];

const TRUST_ITEMS = [
  {
    icon: ShieldCheck,
    title: "Authentic Brands",
    description: "Sourced from trusted brand partners.",
  },
  {
    icon: Truck,
    title: "Reliable Delivery",
    description: "Carefully packed and delivered to you.",
  },
  {
    icon: Heart,
    title: "Loyalty Rewards",
    description: "Earn points every time you shop.",
  },
  {
    icon: PackageCheck,
    title: "Easy Assistance",
    description: "Support when you need it.",
  },
];

function SectionHeader({ eyebrow, title, description, actionLabel, onAction }) {
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        {eyebrow ? (
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-primary-800">
            {eyebrow}
          </p>
        ) : null}

        <h2 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
          {title}
        </h2>

        {description ? (
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-500 sm:text-base">
            {description}
          </p>
        ) : null}
      </div>

      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          className="group inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-primary-800 transition hover:text-primary-950"
        >
          {actionLabel}
          <ArrowRight
            size={16}
            className="transition-transform duration-200 group-hover:translate-x-1"
          />
        </button>
      ) : null}
    </div>
  );
}

function CampaignHero({ campaign, navigate }) {
  const isRightAligned = campaign.align === "right";

  return (
    <div className="relative min-h-[580px] sm:min-h-[650px] lg:min-h-[720px]">
      <div
        className={`absolute inset-0 ${
          isRightAligned
            ? "bg-gradient-to-l from-black/75 via-black/40 to-black/15"
            : "bg-gradient-to-r from-black/75 via-black/40 to-black/15"
        }`}
      />

      <div className="relative mx-auto flex min-h-[580px] max-w-7xl items-end px-5 pb-14 sm:min-h-[650px] sm:px-8 sm:pb-20 lg:min-h-[720px] lg:px-10 lg:pb-24">
        <motion.div
          key={campaign.title}
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className={`max-w-xl ${
            isRightAligned ? "ml-auto text-right" : "mr-auto text-left"
          }`}
        >
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.23em] text-white/80">
            {campaign.eyebrow}
          </p>

          <h1 className="text-5xl font-semibold leading-[0.95] tracking-[-0.045em] text-white sm:text-6xl lg:text-8xl">
            {campaign.title}
          </h1>

          <p
            className={`mt-6 max-w-lg text-base leading-7 text-white/85 sm:text-lg ${
              isRightAligned ? "ml-auto" : ""
            }`}
          >
            {campaign.description}
          </p>

          <div
            className={`mt-9 flex flex-wrap gap-3 ${
              isRightAligned ? "justify-end" : "justify-start"
            }`}
          >
            <button
              type="button"
              onClick={() => navigate(campaign.primaryPath)}
              className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-bold text-slate-950 transition hover:-translate-y-0.5 hover:bg-primary-800 hover:text-white"
            >
              {campaign.primaryLabel}
              <ArrowUpRight size={16} />
            </button>

            <button
              type="button"
              onClick={() => navigate(campaign.secondaryPath)}
              className="rounded-full border border-white/70 bg-white/10 px-6 py-3.5 text-sm font-bold text-white backdrop-blur-sm transition hover:bg-white hover:text-slate-950"
            >
              {campaign.secondaryLabel}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivalProducts, setNewArrivalProducts] = useState([]);
  const [storefrontCategories, setStorefrontCategories] = useState([]);
  const [storefrontBrands, setStorefrontBrands] = useState([]);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllSubCategories, setShowAllSubCategories] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function loadStorefrontCatalogue() {
      try {
        setLoading(true);
        setCatalogError("");

        const [homeResponse, brandsResponse] = await Promise.all([
          apiRequest("/storefront/home"),
          apiRequest("/storefront/brands"),
        ]);

        setFeaturedProducts(homeResponse?.featured || []);
        setNewArrivalProducts(homeResponse?.newArrivals || []);
        setStorefrontCategories(homeResponse?.categories || []);
        setStorefrontBrands(brandsResponse || []);
        setCatalogLoaded(true);
      } catch (error) {
        console.error("Could not load storefront catalogue:", error);

        if (!mounted) return;

        setCatalogError(
          "Live catalogue is temporarily unavailable. Showing preview products.",
        );
        setFeaturedProducts(PRODUCTS.slice(0, 4));
        setNewArrivalProducts(PRODUCTS.slice(4, 8));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadStorefrontCatalogue();

    return () => {
      mounted = false;
    };
  }, []);

  const categoryCards = useMemo(() => {
    if (!storefrontCategories.length) return [];

    return storefrontCategories.map((category) => ({
      ...category,
      image: category.imageKey,
      count: category._count?.products || 0,
    }));
  }, [storefrontCategories]);
  const subCategoryCards = useMemo(() => storefrontCategories.flatMap(category =>
    (category.subCategories || []).map(subCategory => ({ ...subCategory,
      image: subCategory.imageKey || category.imageKey,
      path: `/category/${encodeURIComponent(category.slug || category.id)}?subCategory=${encodeURIComponent(subCategory.slug)}`,
    }))), [storefrontCategories]);

  return (
    <PageWrapper>
      {catalogError ? (
        <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {catalogError}
          </div>
        </div>
      ) : null}

      {/* Hero campaign carousel */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-slate-950">
        {/* Permanent looping background video */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src="/videos/charmsbg.mov" type="video/mp4" />
        </video>

        {/* Slight permanent dark layer for readability */}
        <div className="pointer-events-none absolute inset-0 bg-black/20" />

        {/* Slider content stays in front of the video */}
        <Swiper
          modules={[Autoplay, Pagination]}
          slidesPerView={1}
          loop
          autoplay={{ delay: 5500, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          className="charms-home-hero relative z-10"
        >
          {CAMPAIGNS.map((campaign) => (
            <SwiperSlide key={campaign.title}>
              <CampaignHero campaign={campaign} navigate={navigate} />
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* Small black promotion strip */}
      <section className="bg-slate-950 px-4 py-3 text-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-8 gap-y-2 text-center text-xs font-semibold tracking-wide sm:text-sm">
          <span>Authentic premium brands</span>
          <span className="hidden h-1 w-1 rounded-full bg-white/60 sm:block" />
          <span>Earn loyalty points on every purchase</span>
          <span className="hidden h-1 w-1 rounded-full bg-white/60 sm:block" />
          <span>Fashion for every occasion</span>
        </div>
      </section>

      {/* Men / Women / Kids */}
      <section className="bg-[#f8f7f5] py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Shop the edit"
            title="Find your next favourite look"
            description="Curated collections for every style, every plan and every member of the family."
            actionLabel={categoryCards.length > 6 ? (showAllCategories ? 'Show less' : 'View all') : undefined}
            onAction={() => setShowAllCategories(current => !current)}
          />

          <div className="grid gap-4 md:grid-cols-3 sm:gap-5">
            {(showAllCategories ? categoryCards : categoryCards.slice(0, 6)).map(category => <CategoryCard key={category.id} cat={category} navigate={navigate} />)}
            {!loading && !categoryCards.length && <p className="text-sm text-gray-500">No categories available yet.</p>}
          </div>
        </div>
      </section>

      {/* Live category catalogue */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Browse categories"
            title="More ways to shop"
            actionLabel={subCategoryCards.length > 8 ? (showAllSubCategories ? 'Show less' : 'View all') : undefined}
            onAction={() => setShowAllSubCategories(current => !current)}
          />

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {(showAllSubCategories ? subCategoryCards : subCategoryCards.slice(0, 8)).map((category, index) => (
              <motion.div
                key={category.id || category.name || index}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: index * 0.05, duration: 0.35 }}
              >
                <CategoryCard cat={category} navigate={navigate} />
              </motion.div>
            ))}
          </div>
          {!loading && !subCategoryCards.length && <p className="text-sm text-gray-500">No subcategories available yet.</p>}
        </div>
      </section>

      {/* New arrivals */}
      <section className="bg-[#f8f7f5] py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Just landed"
            title="New arrivals"
            description="Fresh styles added to the CHARMS collection."
            actionLabel="Shop new arrivals"
            onAction={() => navigate("/search/all?sort=newest")}
          />

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {loading
              ? [1, 2, 3, 4].map((item) => <SkeletonCard key={item} />)
              : newArrivalProducts.slice(0, 4).map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ delay: index * 0.07, duration: 0.35 }}
                  >
                    <ProductCard item={item} navigate={navigate} />
                  </motion.div>
                ))}
          </div>

          {!loading && catalogLoaded && newArrivalProducts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center text-sm text-slate-500">
              New arrivals will appear here as soon as they are available
              online.
            </div>
          ) : null}
        </div>
      </section>

      {/* Editorial campaign blocks */}
      <section className="bg-[#faf9f7] py-16 sm:py-24">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
          <div className="grid overflow-hidden bg-slate-950 lg:grid-cols-[0.92fr_1.08fr]">
            <div className="relative flex min-h-[430px] flex-col justify-between overflow-hidden px-7 py-10 text-white sm:px-12 sm:py-14 lg:min-h-[610px] lg:px-16 lg:py-16">
              <div className="absolute -left-32 -top-32 h-72 w-72 rounded-full bg-primary-700/30 blur-3xl" />
              <div className="absolute -bottom-32 right-0 h-72 w-72 rounded-full bg-primary-900/40 blur-3xl" />

              <div className="relative z-10">
                <div className="flex items-center gap-3">
                  <span className="h-px w-9 bg-white/60" />
                  <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/65">
                    CHARMS Rewards
                  </p>
                </div>

                <h2 className="mt-8 max-w-md text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                  Style more.
                  <br />
                  Earn more.
                </h2>

                <p className="mt-6 max-w-md text-sm leading-7 text-white/70 sm:text-base">
                  Every CHARMS purchase brings you closer to your next favourite
                  piece. Earn loyalty points, then use them when you find
                  something worth taking home.
                </p>
              </div>

              <div className="relative z-10 mt-10">
                <button
                  type="button"
                  onClick={() => navigate("/search/all")}
                  className="group inline-flex items-center gap-4 border border-white bg-white px-6 py-4 text-xs font-bold uppercase tracking-[0.14em] text-slate-950 transition hover:border-primary-800 hover:bg-primary-800 hover:text-white"
                >
                  Start Shopping
                  <span className="flex h-7 w-7 items-center justify-center border border-current transition-transform duration-300 group-hover:translate-x-1">
                    <ArrowRight size={15} />
                  </span>
                </button>

                <p className="mt-5 text-xs leading-5 text-white/50">
                  Loyalty points are available to registered CHARMS customers.
                </p>
              </div>
            </div>

            <div className="relative min-h-[430px] overflow-hidden lg:min-h-[610px]">
              <img
                src="https://images.unsplash.com/photo-1496747611176-843222e1e57c?auto=format&fit=crop&w=1600&q=90"
                alt="CHARMS rewards fashion collection"
                className="absolute inset-0 h-full w-full object-cover transition duration-1000 hover:scale-105"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />

              <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-6 text-white sm:p-9">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">
                    A little extra
                  </p>
                  <p className="mt-2 text-xl font-semibold tracking-tight sm:text-2xl">
                    More reasons to return.
                  </p>
                </div>

                <span className="hidden border border-white/50 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em] sm:block">
                  CHARMS Club
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="The CHARMS favourites"
            title="Trending now"
            description="The products customers are loving right now."
            actionLabel="Explore all products"
            onAction={() => navigate("/search/all")}
          />

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {loading
              ? [1, 2, 3, 4].map((item) => <SkeletonCard key={item} />)
              : featuredProducts.slice(0, 4).map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ delay: index * 0.07, duration: 0.35 }}
                  >
                    <ProductCard item={item} navigate={navigate} />
                  </motion.div>
                ))}
          </div>

          {!loading && catalogLoaded && featuredProducts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-[#f8f7f5] px-6 py-14 text-center text-sm text-slate-500">
              Featured products will appear here as soon as they are available
              online.
            </div>
          ) : null}
        </div>
      </section>

      {/* Brand list */}
      <section className="border-y border-[#eadde1] bg-[#faf9f7] py-16 sm:py-24">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
          <div className="flex flex-col gap-6 border-b border-[#eadde1] pb-8 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-primary-800" />
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-primary-800">
                  Curated Brands
                </p>
              </div>

              <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                The labels you love.
              </h2>
            </div>

            <p className="max-w-sm text-sm leading-6 text-slate-500 sm:text-right">
              Explore authentic collections from the fashion brands you trust.
            </p>
          </div>

          {loading ? (
            <div className="mt-8 grid grid-cols-2 border-l border-t border-[#eadde1] sm:grid-cols-3 lg:grid-cols-6">
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <div
                  key={item}
                  className="aspect-[4/3] animate-pulse border-b border-r border-[#eadde1] bg-white"
                />
              ))}
            </div>
          ) : storefrontBrands.length > 0 ? (
            <div className="mt-8 grid grid-cols-2 border-l border-t border-[#eadde1] sm:grid-cols-3 lg:grid-cols-6">
              {storefrontBrands.map((brand, index) => {
                const brandImage = getImageUrl(brand.logoKey);

                return (
                  <motion.button
                    key={brand.id}
                    type="button"
                    onClick={() =>
                      navigate(`/search/${encodeURIComponent(brand.name)}`)
                    }
                    whileHover={{ backgroundColor: "#ffffff" }}
                    transition={{ duration: 0.22 }}
                    className="group relative flex aspect-[4/3] flex-col items-center justify-center overflow-hidden border-b border-r border-[#eadde1] bg-[#fdfcfa] px-4 text-center"
                  >
                    <span className="absolute left-3 top-3 text-[9px] font-bold tracking-[0.12em] text-slate-300 transition group-hover:text-primary-300">
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <div className="flex h-14 w-full items-center justify-center sm:h-16">
                      {brandImage ? (
                        <img
                          src={brandImage}
                          alt={brand.name}
                          className="max-h-full max-w-[125px] object-contain grayscale opacity-75 transition duration-500 group-hover:scale-105 group-hover:grayscale-0 group-hover:opacity-100"
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <span className="text-3xl font-semibold tracking-tight text-slate-800 transition group-hover:text-primary-800">
                          {brand.name?.charAt(0)}
                        </span>
                      )}
                    </div>

                    <div className="mt-5 flex items-center gap-2">
                      <span className="h-px w-0 bg-primary-800 transition-all duration-300 group-hover:w-4" />
                      <p className="line-clamp-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-600 transition group-hover:text-primary-800">
                        {brand.name}
                      </p>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          ) : (
            <div className="mt-8 border border-dashed border-[#eadde1] bg-white px-6 py-14 text-center">
              <p className="text-sm text-slate-500">
                No brands are currently available online.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Compact service reasons */}
      <section className="bg-white py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {TRUST_ITEMS.map((item) => {
              const Icon = item.icon;

              return (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-800">
                    <Icon size={20} />
                  </div>

                  <div>
                    <h3 className="font-semibold text-slate-950">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-slate-500">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-slate-950 px-4 py-16 text-center text-white sm:py-20">
        <div className="mx-auto max-w-2xl">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white">
            <Sparkles size={21} />
          </div>

          <p className="mt-5 text-xs font-bold uppercase tracking-[0.22em] text-white/65">
            Find your style
          </p>

          <h2 className="mt-3 text-4xl font-semibold tracking-tight sm:text-5xl">
            Your wardrobe refresh starts here.
          </h2>

          <p className="mt-5 text-base leading-7 text-white/70">
            Explore premium fashion from the brands you trust at CHARMS.
          </p>

          <button
            type="button"
            onClick={() => navigate("/search/all")}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-slate-950 transition hover:bg-primary-700 hover:text-white"
          >
            Shop the collection
            <ArrowRight size={16} />
          </button>
        </div>
      </section>
    </PageWrapper>
  );
}
